import React, { useState } from 'react';
import { Box, Typography, Stack, Paper, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import BusinessListings from './BusinessListings';
import HeatmapMapView from './HeatmapMapView';

import logger from '../../utils/logger';

// Mobile-First Styled Components
const MapContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  overflow: 'hidden',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    borderRadius: '12px',
    border: '0.6px solid #F6F0F0',
    boxShadow: 'none',
  },
}));

const MobileTabsContainer = styled(Box)(({ theme }) => ({
  display: 'block',
  borderBottom: '1px solid #E2E8F0',
  backgroundColor: '#F8FAFC',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '44px',
  '& .MuiTabs-indicator': {
    backgroundColor: '#0B91D6',
    height: '3px',
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'none',
  minHeight: '44px',
  flex: 1,
  '&.Mui-selected': {
    color: '#0B91D6',
    fontWeight: 600,
  },
}));

const MapContent = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  height: 'auto',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    height: '590px',
  },
  [theme.breakpoints.down('lg')]: {
    [theme.breakpoints.up('md')]: {
      height: '500px',
    },
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '12px',
  borderBottom: '1px solid #E2E8F0',
  backgroundColor: '#ffffff',
  maxHeight: '280px',
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  [theme.breakpoints.up('sm')]: {
    padding: '16px',
    maxHeight: '320px',
  },
  [theme.breakpoints.up('md')]: {
    width: '378px',
    padding: '16px',
    borderRight: '0.6px solid #F6F0F0',
    borderBottom: 'none',
    maxHeight: 'none',
    overflow: 'auto',
  },
  [theme.breakpoints.down('lg')]: {
    [theme.breakpoints.up('md')]: {
      width: '320px',
    },
  },
}));

const MapPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  backgroundImage: 'url(/images/heatmap-results-background.svg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '300px',
  height: '300px',
  touchAction: 'pan-x pan-y',
  [theme.breakpoints.up('sm')]: {
    minHeight: '350px',
    height: '350px',
  },
  [theme.breakpoints.up('md')]: {
    minHeight: '500px',
    height: '590px',
    width: 'auto',
  },
  [theme.breakpoints.down('lg')]: {
    [theme.breakpoints.up('md')]: {
      height: '500px',
    },
  },
}));

const HeatmapMapSection = ({ mapData, businessName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <MapContainer>
      {/* Mobile Tabs */}
      <MobileTabsContainer>
        <StyledTabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <StyledTab label="Map View" />
          <StyledTab label="Businesses" />
        </StyledTabs>
      </MobileTabsContainer>

      <MapContent>
        {/* Left Panel - Business Listings */}
        {(!isMobile || activeTab === 1) && (
          <LeftPanel sx={{ display: isMobile && activeTab !== 1 ? 'none' : 'block' }}>
            <BusinessListings
              businesses={mapData.businesses}
              businessName={businessName}
            />
          </LeftPanel>
        )}

        {/* Map Panel */}
        {(!isMobile || activeTab === 0) && (
          <MapPanel sx={{ display: isMobile && activeTab !== 0 ? 'none' : 'block' }}>
            {logger.log('Map Section - Passing to HeatmapMapView:', {
              pinsCount: mapData.pins?.length,
              center: mapData.center,
              radius: mapData.radius
            })}
            <HeatmapMapView
              pins={mapData.pins}
              center={mapData.center}
              radius={mapData.radius || 2}
            />
          </MapPanel>
        )}
      </MapContent>
    </MapContainer>
  );
};

export default HeatmapMapSection;